import { Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { ConversationStatus } from '@provision/conversation-types';
import { DynamicForm, DynamicIcon, EllipsesMenu } from 'src/components';
import { SidePanelCaption } from 'src/components/SidePanelCaption';
import { SubscriptionDetailsPanel } from 'src/components/SidePanelTabs/MembershipInformation/subscription-details-panel';
import { isSubscriptionInPsmPhase } from 'src/components/SidePanelTabs/MembershipInformation/subscription-details.utils';
import { useMembershipInfoForm } from 'src/components/SidePanelTabs/MembershipInformation/use-membership-info-form';
import { useMembershipInfoMenu } from 'src/components/SidePanelTabs/MembershipInformation/use-membership-info-menu';
import { EXTENDED_VIEW_CHILD_MAX_WIDTH } from 'src/constants/layout';
import { Address, Conversation, PartnerGroupRules, usePartnerGroupRules } from 'src/graphql';
import { useStripeSubscriptionDetails } from 'src/graphql/entities/stripe-subscription-details';
import { useStripeSubscriptionSchedule } from 'src/graphql/entities/stripe-subscription-schedule';
import { useSubscriptionPause } from 'src/graphql/entities/subscription-pause';

interface MembershipInformationProps {
  address: Partial<Address>;
  conversation?: Conversation;
}

const MembershipInformationSkeleton = () => {
  const theme = useTheme();
  return (
    <Stack
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-end',
        width: '100%',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(2),
      }}
    >
      <Skeleton width="100%" height={45} />
      {Array.from(Array(5).keys()).map((i) => (
        <Stack key={i} direction="row" justifyContent="space-between">
          <Skeleton width="37%" height={40} />
          <Skeleton width="60%" height={40} />
        </Stack>
      ))}
      <Skeleton width="100%" height={80} />
    </Stack>
  );
};

export const MembershipInformation = ({
  conversation,
  address,
}: MembershipInformationProps) => {
  const theme = useTheme();
  const disabled =
    (conversation?.conversationStatusId! as ConversationStatus) ===
      ConversationStatus.Closed || !!address.isDeleted;
  const { partnerGroupRules } = usePartnerGroupRules(
    address.partner?.id ?? address.partnerId ?? conversation?.partnerId,
  );
  const hasAccessToMembershipInfo =
    partnerGroupRules[PartnerGroupRules.AccessToSubscriptionManagement];

  const {
    stripeSubscriptionDetails: subscriptionDetails,
    loading: loadingSubscriptionDetails,
  } = useStripeSubscriptionDetails({ address });

  const {
    addComplimentaryPeriod,
    addPreSoldMembership,
    loading: isUpdatingSchedule,
  } = useStripeSubscriptionSchedule();

  const hasPsmAsCurrentPhase = isSubscriptionInPsmPhase(address);

  const { unpauseSubscription, loading: isPauseLoading } = useSubscriptionPause();

  const {
    onSubmit: onSubmitMembershipInfo,
    formFields: membershipInfoFormFields,
    loading: loadingMembershipInfo,
  } = useMembershipInfoForm({
    conversation,
    address,
    disabled,
    addCppToSchedule: addComplimentaryPeriod,
    isCppDisabled: hasPsmAsCurrentPhase,
    subscriptionDetails,
  });

  const { menuOptions } = useMembershipInfoMenu({
    conversation,
    address: address as Address,
    subscriptionDetails,
    addCppToSchedule: addComplimentaryPeriod,
    addPsmToSchedule: addPreSoldMembership,
    onUnpauseSubscription: () => {
      if (address?.addressId && address?.partnerId) {
        void unpauseSubscription(
          address.addressId,
          address.partnerId,
          subscriptionDetails?.plan,
        );
      }
    },
    isCppDisabled: hasPsmAsCurrentPhase,
  });

  if (
    loadingMembershipInfo ||
    loadingSubscriptionDetails ||
    isUpdatingSchedule ||
    isPauseLoading
  ) {
    return <MembershipInformationSkeleton />;
  }

  if (!hasAccessToMembershipInfo) {
    return (
      <Stack alignItems="center" justifyContent="center" padding={2}>
        <DynamicIcon
          name="alert-circle"
          library="lucide"
          size={32}
          color={theme.palette.error.main}
          sx={{ cursor: 'default' }}
        />
        <Typography
          variant="body1"
          color="textSecondary"
          textAlign="center"
          mt={1}
          sx={{ whiteSpace: 'normal', maxWidth: '80%' }}
        >
          Partner is not using OneVision&apos;s Subscription Management Services
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack sx={{ maxWidth: EXTENDED_VIEW_CHILD_MAX_WIDTH }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <SidePanelCaption paddingBottom={1}>Membership Information</SidePanelCaption>
        {menuOptions.length > 0 && !address.isDeleted ? (
          <EllipsesMenu
            options={menuOptions}
            icon="more-vertical"
            iconLibrary="lucide"
            triggerTestId="membership-info-actions-trigger"
          />
        ) : null}
      </Stack>

      <DynamicForm
        watchable
        watchableSubmitOnlyChangedField
        fields={membershipInfoFormFields}
        onSubmit={onSubmitMembershipInfo}
        sx={{ gap: 0 }}
      />

      <SubscriptionDetailsPanel subscriptionDetails={subscriptionDetails} />
    </Stack>
  );
};
